import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CircleCheck,
  CircleOff,
  FileDown,
  FileText,
  Layers,
  Pencil,
  Plus,
  Settings2,
  Trash2,
  Users,
} from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import useFeatureToggle from '../../components/useFeatureToggle'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import { DeniedState, ErrorState } from '../../components/States'
import RowActions from '../../components/RowActions'
import SectionNav from '../../components/SectionNav'
import StatStrip from '../../components/StatStrip'
import useHashTab from '../../components/useHashTab'
import { useToast } from '../../components/toastContext'
import { dateColumn, flagColumn, indexColumn, statusColumn } from './cells'
import {
  deletePolicy,
  getPoliciesPage,
  listPolicies,
  setPoliciesEnabled,
  setPolicyState,
} from './engagementService'
import PolicyCategoriesPanel from './PolicyCategoriesPanel'
import PolicySettingsPanel from './PolicySettingsPanel'
import PolicyUsersDrawer from './PolicyUsersDrawer'
import { engagementStats as ST, policiesStrings as S } from './strings'
import './engagement.css'
import { serverDetail, serverText } from '../../lib/i18n'

/* `categories` is no longer a tab; it stays known so an old #categories link opens the
   settings, where the categories now live. */
const SECTIONS = ['policies', 'categories', 'settings']

/**
 * إدارة سياسات الأمن السيبراني — the legacy `WorkspacePoliciesSettings`.
 *
 * TWO SECTIONS, as the legacy screen has two tabs: the policies themselves, and the
 * settings that decide how a user meets them — with the categories they are filed under
 * at the foot of the settings, where the legacy keeps them.
 *
 * WHAT IS NOT WIRED: `إرسال بريد`. The legacy `SendEmail` has no counterpart in the API and
 * the button is registered in COSMETIC_UI.md. Everything else on the screen is live.
 */
export default function PoliciesPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [hashTab, setTab] = useHashTab('policies', SECTIONS)
  /* The categories were a tab of their own and now sit under the settings, as in the
     legacy; a link to the old #categories still lands on them. */
  const tab = hashTab === 'categories' ? 'settings' : hashTab
  /* REMEMBERED, not plain state: the page came back on a Back and the search that chose
     those rows did not, so page 3 returned over a list nobody had searched. */
  const [query, setQuery] = useRemembered('policies:query', '')
  const [rows, setRows] = useState([])
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one: without this the table renders its "no records"
     state for a database fault, which tells the operator the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingState, setPendingState] = useState(null)
  /** The policy whose users are open, or `null`. */
  const [usersOf, setUsersOf] = useState(null)
  /* Same for the scope card — it decides WHICH policies those page numbers count. */
  const [scope, setScope] = useRemembered('policies:scope', null)

  /* The form is its own page (#1412): two names, two documents, an enforcement date
     and a picker over a directory of thousands is not a sheet's worth of screen. The
     row travels with the click so editing opens filled in without a second request. */
  const openForm = useCallback(
    (policy) =>
      navigate(policy ? `/policies/${policy.id}/edit` : '/policies/new', {
        state: policy ? { policy } : undefined,
      }),
    [navigate],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([listPolicies(), getPoliciesPage()])
      .then(([list, page]) => {
        if (cancelled) return
        setLoadError(null)
        setRows(list.rows)
        setSettings(page)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey, toast])

  /* Shared with every other activation switch — see useFeatureToggle. The flag
     lives inside `settings` here, which is why the hook takes a setter. */
  const { toggle: toggleFeature } = useFeatureToggle({
    setValue: (enabled) => setSettings((previous) => ({ ...previous, enabled })),
    save: setPoliciesEnabled,
  })

  const applyState = async () => {
    const policy = pendingState
    setPendingState(null)
    const outcome = await setPolicyState(policy.id, !policy.isActive)
    if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.save)
    toast.success(serverText(outcome.message) || S.saved)
    setReloadKey((key) => key + 1)
  }

  const remove = async () => {
    const policy = pendingDelete
    setPendingDelete(null)
    const outcome = await deletePolicy(policy.id)
    if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.delete)
    toast.success(serverText(outcome.message) || S.deleted)
    setReloadKey((key) => key + 1)
  }

  /* Counted off the rows already on the page; the two state cards narrow the
     list to what they count. */
  const stats = useMemo(() => {
    const active = rows.filter((row) => row.isActive).length
    return { total: rows.length, active, inactive: rows.length - active }
  }, [rows])

  const visibleRows = useMemo(() => {
    if (scope === 'active') return rows.filter((row) => row.isActive)
    if (scope === 'inactive') return rows.filter((row) => !row.isActive)
    return rows
  }, [rows, scope])

  const columns = useMemo(
    () => [
      indexColumn(S.columns.index),
      { key: 'name', header: S.columns.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'nameEn',
        header: S.columns.nameEn,
        render: (row) => <bdi dir="ltr">{row.nameEn}</bdi>,
      },
      { key: 'version', header: S.columns.version, align: 'center' },
      dateColumn('createdOn', S.columns.createdOn),
      flagColumn('forceUsers', S.columns.forceUsers, S),
      dateColumn('evaluationStartDate', S.columns.evaluationStartDate),
      statusColumn('isActive', S.columns.status, S),
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '130px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              /* The legacy `/UsersPoliciesDetails` screen, as a drawer over this list. */
              {
                id: 'users',
                icon: Users,
                label: S.users.open,
                tone: 'violet',
                onClick: () => setUsersOf(row),
              },
              {
                id: 'state',
                /* The disabled / enabled pair as it is drawn everywhere: a struck circle to
                   disable a live policy, a ticked one to enable an inactive one. Two
                   glyphs, so it never looks the same both ways as `Power` did; and never
                   a paused circle, since a policy is not paused, it is out of force. The
                   stat tiles above the table count the two states with the same pair. */
                icon: row.isActive ? CircleOff : CircleCheck,
                label: row.isActive ? S.state.deactivate : S.state.activate,
                /* The colours the status tiles above the table already use for these two
                   states — amber paused, green live. */
                tone: row.isActive ? 'warning' : 'success',
                onClick: () => setPendingState(row),
              },
              /* THE TWO DOCUMENTS, as the legacy row menu offers them: each opens in a
                 new tab, where the browser previews the PDF and offers to save it. An
                 entry is only there when that language HAS a file — a policy can be
                 saved with one of the two. */
              row.fileAr && {
                id: 'fileAr',
                icon: FileDown,
                label: S.row.fileAr,
                tone: 'primary',
                as: 'a',
                href: row.fileAr,
                target: '_blank',
                rel: 'noreferrer',
              },
              row.fileEn && {
                id: 'fileEn',
                icon: FileDown,
                label: S.row.fileEn,
                tone: 'primary',
                as: 'a',
                href: row.fileEn,
                target: '_blank',
                rel: 'noreferrer',
              },
              /* `إرسال بريد` IS NOT HERE. Nothing in this slice sends one — the
                 legacy's send action has no counterpart in the port, and a mail button
                 that opens nothing is worse than its absence. COSMETIC_UI.md. */
              { id: 'edit', icon: Pencil, label: S.row.edit, onClick: () => openForm(row) },
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [openForm],
  )

  const sectionGroups = useMemo(
    () => [
      {
        items: [
          { id: 'policies', label: S.sections.policies, icon: FileText },
          { id: 'settings', label: S.sections.settings, icon: Settings2 },
        ],
      },
    ],
    [],
  )

  if (loadError) {
    return (
      <>
        <PageHeader title={S.title} />
        {loadError.denied ? (
          <DeniedState description={loadError.message} />
        ) : (
          <ErrorState
            title={S.actionFailed}
            description={loadError.message}
          />
        )}
      </>
    )
  }

  if (loading || !settings) {
    return (
      <>
        <PageHeader title={S.title} />
        <PageLoader />
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={S.title}
        count={tab === 'policies' ? rows.length : undefined}
        actions={
          tab === 'policies' && (
            <Button onClick={() => openForm(null)}>
              <Plus size={16} strokeWidth={2} />
              {S.create}
            </Button>
          )
        }
      />

      <div className="eng__workspace">
        <SectionNav
          groups={sectionGroups}
          value={tab}
          onChange={setTab}
          ariaLabel={S.title}
        />

        <div className="eng__panel">
          {tab === 'policies' && (
            <>
              <StatStrip
                label={ST.caption}
                activeId={scope ?? 'all'}
                onSelect={(id) => {
                  /* ALL IS NOT A FILTER TO TOGGLE, it is the absence of one, so it clears
                     the scope outright rather than flipping it back on when clicked
                     twice. */
                  if (id === 'all') return setScope(null)
                  setScope((current) => (current === id ? null : id))
                }}
                items={[
                  {
                    /* Was an unclickable figure labelled "Total": the only way back to the
                       full list was to click the selected card a second time. */
                    id: 'all',
                    icon: Layers,
                    label: S.allPolicies,
                    value: stats.total,
                    tone: 'violet',
                  },
                  {
                    id: 'active',
                    icon: CircleCheck,
                    label: ST.active,
                    value: stats.active,
                    tone: 'success',
                  },
                  {
                    id: 'inactive',
                    icon: CircleOff,
                    label: ST.inactive,
                    value: stats.inactive,
                    tone: 'warning',
                  },
                ]}
              />

              <FilterBar
                search={{ value: query, onChange: setQuery, placeholder: S.columns.name }}
              />

              <DataTable
                error={loadError}
                columns={columns}
                rows={visibleRows}
                query={query}
                /* The scope card sits outside the table, which cannot see it — narrowing
                   to the inactive policies kept a page the shorter list does not have. */
                resetKey={scope}
                controls={false}
                getRowId={(row) => row.id}
                initialSort={{ key: 'name', dir: 'asc' }}
              />
            </>
          )}

          {tab === 'settings' && (
            <>
              <PolicySettingsPanel
                settings={settings}
                onChange={(patch) => setSettings((previous) => ({ ...previous, ...patch }))}
                enabled={settings.enabled}
                onToggle={toggleFeature}
              />
              <PolicyCategoriesPanel />
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={!!pendingState}
        title={pendingState?.isActive ? S.deactivateTitle : S.activateTitle}
        tone={pendingState?.isActive ? 'warning' : 'success'}
        message={pendingState?.isActive ? S.deactivateConfirm : S.activateConfirm}
        confirmLabel={pendingState?.isActive ? S.state.deactivate : S.state.activate}
        cancelLabel={S.close}
        onConfirm={applyState}
        onCancel={() => setPendingState(null)}
      />

      <PolicyUsersDrawer open={!!usersOf} policy={usersOf} onClose={() => setUsersOf(null)} />
    </>
  )
}
